import React, { useState, useEffect, useRef } from 'react'

import { Text, Icon, IconText, Input, Loader } from '@components/UI'
import { ActionTypes, Files } from '@constants'
import { useFileDelete, useFileUpdate } from '@components/Classroom/hooks'

import { formatFileType } from '../../helpers'

const File = ({
  data,
  status,
  load,
  theme = 'dark',
  downloadable = false,
  preview = false
}) => {
  const [showControls, setShowControls] = useState(false)
  const [initial, setInitial] = useState(null)
  const [newName, setNewName] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const { mutateAsync: deleteFile } = useFileDelete()
  const { mutateAsync: updateFile } = useFileUpdate()

  const input = useRef(null)

  useEffect(() => {
    if (showControls) {
      const handler = () => {
        setShowControls(false)
      }
      window.addEventListener('click', handler)

      return () => {
        window.removeEventListener('click', handler)
      }
    }
  }, [showControls])

  useEffect(() => {
    if (deleting) {
      const handler = () => {
        setDeleting(false)
      }
      window.addEventListener('click', handler)

      return () => {
        window.removeEventListener('click', handler)
      }
    }
  }, [deleting])

  const conversionInProgress = (data) => {
    return (
      data.conversion !== undefined && data.conversion.status === 'IN_PROGRESS'
    )
  }

  return (
    <div
      className={`gp-finder-file ${
        (status === ActionTypes.STATUS.PENDING &&
          data.progress !== undefined) ||
        deleting
          ? 'disabled'
          : ''
      } ${showControls ? 'active' : ''} ${theme} ${
        status !== ActionTypes.STATUS.PENDING && downloadable
          ? 'downloadable'
          : ''
      }`}
    >
      <div
        className={`wrapper ${
          preview &&
          Files.SUPPORTED_FILE_TYPES.IMAGES.includes(data.type) &&
          status !== ActionTypes.STATUS.PENDING
            ? 'preview'
            : ''
        }`}
        onClick={() => {
          if (
            (status === ActionTypes.STATUS.PENDING &&
              data.progress !== undefined) ||
            conversionInProgress(data)
          )
            return
          load(data)
        }}
      >
        {preview &&
        Files.SUPPORTED_FILE_TYPES.IMAGES.includes(data.type) &&
        status !== ActionTypes.STATUS.PENDING ? (
          <img src={data.data} />
        ) : (
          <React.Fragment>
            <div
              className={`file-icon-container ${
                !['note', 'sketch', 'code'].includes(data.type) &&
                status !== ActionTypes.STATUS.PENDING
                  ? 'with-border'
                  : ''
              }`}
            >
              {(status === ActionTypes.STATUS.PENDING &&
                data.progress !== undefined) ||
              conversionInProgress(data) ? (
                <React.Fragment>
                  <svg
                    className='progress'
                    width='48'
                    height='48'
                    viewBox='0 0 48 48'
                  >
                    <circle
                      cx='24'
                      cy='24'
                      r='22'
                      fill='none'
                      stroke={
                        theme === 'light'
                          ? 'rgba(45, 48, 75, .1)'
                          : 'rgba(255, 255, 255, .1)'
                      }
                      strokeWidth='4'
                    />
                    <circle
                      className='value'
                      cx='24'
                      cy='24'
                      r='22'
                      fill='none'
                      stroke={
                        theme === 'light'
                          ? 'rgba(45, 48, 75, .75)'
                          : 'rgba(255, 255, 255, 1)'
                      }
                      strokeWidth='4'
                      strokeDasharray={Files.CIRCUMFERENCE}
                      strokeDashoffset={
                        (Files.CIRCUMFERENCE * (100 - data.progress)) / 100
                      }
                      strokeLinecap='round'
                    />
                  </svg>
                  {conversionInProgress(data) ? (
                    <Loader icon full dark />
                  ) : (
                    <Text className='progress-status color-f text-a'>
                      {data.progress}%
                    </Text>
                  )}
                </React.Fragment>
              ) : (
                <Icon
                  name={formatFileType(data.type).icon}
                  className='file-icon'
                />
              )}
            </div>
            {status !== ActionTypes.STATUS.PENDING && downloadable && (
              <a
                href={data.data}
                download={data.name}
                className='download-btn'
                rel='noopener noreferrer'
                target='_blank'
              >
                <Icon name='download' />
              </a>
            )}
            {initial === data.name ? (
              <Input
                autoFocus
                reference={input}
                defaultValue={data.name}
                events={{
                  onClick: (e) => e.stopPropagation(),
                  onChange: (v) => setNewName(v),
                  onBlur: () => {
                    const reset = () => {
                      setInitial(null)
                      setNewName(null)
                    }
                    if (newName !== name && newName) {
                      updateFile({
                        id: data.id,
                        data: { name: { action: 'replace', value: newName } }
                      }).then(() => reset)
                    } else {
                      reset()
                    }
                  },
                  onKeyPress: (e) => {
                    if (e.key === 'Enter') input.current.blur()
                  }
                }}
              />
            ) : (
              <Text className='text-d file-name'>{data.name}</Text>
            )}
          </React.Fragment>
        )}
      </div>
      <div className={`control-container ${theme}`}>
        <button
          onClick={() => {
            setShowControls(true)
          }}
        >
          <Icon name='three dots vertical' />
        </button>
        {showControls && (
          <div className='controls-container'>
            <div>
              <button
                onClick={() => {
                  setInitial(data.name)
                  setNewName(data.name)
                }}
              >
                <IconText value='Rename' icon='pencil' group='a' />
              </button>
              <button onClick={() => setDeleting(true)}>
                <IconText value='Delete' icon='trash' group='a' />
              </button>
            </div>
          </div>
        )}
      </div>
      {deleting && (
        <div
          className='confirmation-container'
          onClick={(e) => e.stopPropagation()}
        >
          <Text>You sure?</Text>
          <button className='yes' onClick={() => deleteFile({ id: data.id })}>
            Yes
          </button>
          <button className='no' onClick={() => setDeleting(false)}>
            No
          </button>
        </div>
      )}
    </div>
  )
}

export default File
